import { createContext, useContext } from 'react';
import { translations, collectionNameKeys, type Language, type Translations } from './translations';

// Detect browser language and map to supported language
function detectLanguage(): Language {
  const browserLang = navigator.language.split('-')[0].toLowerCase();

  const supported: Language[] = ['en', 'fr', 'it', 'de', 'es', 'tr', 'pt', 'ru', 'zh', 'ja', 'ko'];

  if (supported.includes(browserLang as Language)) {
    return browserLang as Language;
  }

  return 'en';
}

// Storage key for language preference
const LANGUAGE_KEY = 'jigsaw_language';

// Get saved language or detect from browser
export function getInitialLanguage(): Language {
  const saved = localStorage.getItem(LANGUAGE_KEY);
  if (saved && translations[saved as Language]) {
    return saved as Language;
  }
  return detectLanguage();
}

// Save language preference
export function saveLanguage(lang: Language): void {
  localStorage.setItem(LANGUAGE_KEY, lang);
}

// Get translation function
export function getTranslations(lang: Language): Translations {
  return translations[lang] || translations.en;
}

// Format string with placeholders like "Level {current} of {total}"
export function formatString(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`));
}

// Get translated collection name by ID
export function getCollectionName(lang: Language, collectionId: string): string | null {
  const key = collectionNameKeys[collectionId];
  if (key) {
    return translations[lang][key];
  }
  return null;
}

// Context for React components
interface I18nContextValue {
  lang: Language;
  t: Translations;
  setLanguage: (lang: Language) => void;
  formatString: (template: string, values: Record<string, string | number>) => string;
  getCollectionName: (collectionId: string) => string | null;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

export { type Language, type Translations } from './translations';
export { translations } from './translations';
